import { useEffect } from "react";
import { io } from "socket.io-client";
import Link from "next/link";
import { useCustomToastQueue } from "@/hooks/useCustomToastQueue";
import NoImageFound from "@/components/NoImageFound";
import { formatPrice } from "@/lib/utils";
import { useSocket } from "@/components/WebSocket/SocketProvider";
import Image from "next/image";
import { getAuctionItemLink } from "@/lib/links";
import useNotificationStore from "@/stores/useNotificationStore";
import { AuctionItem } from "@/types/auction";

// Define TypeScript interfaces for our notification structures
interface NotificationMeta {
  notificationId?: number;
  itemId: number;
  bidValue: number;
  bidType: string;
  leadingBid: number;
  bidAction: string;
  title: string;
  message: string;
  [key: string]: any;
}

interface WebSocketAuctionItem {
  itemId: string;
  itemTitle: string;
  itemPartId: string;
  itemEndTime: string;
  images?: string[];
  [key: string]: any;
}

interface Notification {
  id: string;
  type: string;
  action: string;
  meta: NotificationMeta;
  theme: string;
  read: boolean;
  created: string;
  isDismissable: boolean;
  alertAsNotification: boolean;
  sticky: boolean;
  data: {
    auctionItem: Partial<AuctionItem>;
  };
}

interface PageNotification {
  id: number;
  action: string;
  alertAsNotification: boolean;
  created: string;
  data: {
    auctionItem: AuctionItem;
  };
  isDismissable: boolean;
  meta: NotificationMeta;
  read: boolean;
  sticky: boolean;
  theme: string;
  type: string;
}

interface NotificationStore {
  totalHits: number;
  notifications: Notification[];
  unreadCount: number;
  allowedTypes: string[];
}

export const useBidNotification = (userId?: string) => {
  const toasterQue = useCustomToastQueue(1, 10000);
  const socket = useSocket();
  const { notificationData, setNotificationData } = useNotificationStore();

  // Direct access to the notification store to bypass React render cycle
  const getStoreData = (): NotificationStore => {
    return useNotificationStore.getState()
      .notificationData as NotificationStore;
  };

  // Direct setter for the notification store to bypass React render cycle
  const setStoreData = (data: NotificationStore) => {
    useNotificationStore.setState({ notificationData: data });
  };

  const initializeFromPageData = (
    pageNotifications: PageNotification[],
    totalHits: number,
    unreadCount: number,
    allowedTypes: string[]
  ) => {
    const convertedNotifications: Notification[] = pageNotifications.map(
      (pageNotif) => ({
        id: pageNotif.id.toString(),
        type: pageNotif.type,
        action: pageNotif.action,
        meta: pageNotif.meta,
        theme: pageNotif.theme,
        read: pageNotif.read,
        created: pageNotif.created,
        isDismissable: pageNotif.isDismissable,
        alertAsNotification: pageNotif.alertAsNotification,
        sticky: pageNotif.sticky,
        data: {
          auctionItem: pageNotif.data.auctionItem,
        },
      })
    );

    const newStore: NotificationStore = {
      totalHits,
      notifications: convertedNotifications,
      unreadCount,
      allowedTypes,
    };

    setStoreData(newStore);
  };

  const addNotification = (notification: Notification) => {
    const currentStore = getStoreData();

    if (!currentStore || !Array.isArray(currentStore?.notifications)) {
      const newStore: NotificationStore = {
        totalHits: 1,
        notifications: [notification],
        unreadCount: 1,
        allowedTypes: ["bid", "auction"],
      };

      setStoreData(newStore);
      return;
    }

    const exists = currentStore.notifications.some(
      (n) => n.id === notification.id
    );
    if (exists) {
      return;
    }

    const updatedStore: NotificationStore = {
      ...currentStore,
      totalHits: currentStore.totalHits + 1,
      unreadCount: currentStore.unreadCount + 1,
      notifications: [notification, ...currentStore.notifications],
    };

    setStoreData(updatedStore);
  };

  const logStoreState = () => {
    const currentStore = getStoreData();

    if (currentStore && Array.isArray(currentStore.notifications)) {
      const readCount = currentStore.notifications.filter((n) => n.read).length;
      const unreadCount = currentStore.notifications.filter(
        (n) => !n.read
      ).length;
    }
  };

  useEffect(() => {
    if (!socket || !userId) return;

    socket.on("connect", () => {
      console.log("Socket connected with ID:", socket.id);
    });

    socket.on("disconnect", (reason) => {
      console.log("Socket disconnected:", reason);
    });

    socket.on("connect_error", (error) => {
      console.log("Socket connection error:", error);
    });

    socket.on(`bid_notification_user_${userId}`, (payload) => {
      const directData = payload;
      const nestedData = payload.data;

      let auctionItem: WebSocketAuctionItem, meta: NotificationMeta;

      if (directData && directData.auctionItem && directData.meta) {
        auctionItem = directData.auctionItem;
        meta = directData.meta;
      } else if (nestedData && nestedData.auctionItem && nestedData.meta) {
        auctionItem = nestedData.auctionItem;
        meta = nestedData.meta;
      } else {
        console.error("Unexpected data structure:", payload);
        return;
      }

      // Generate a unique notification ID that includes bid value and timestamp to ensure
      // multiple outbid notifications on the same item are treated as separate notifications
      const notificationId = meta.notificationId
        ? `${meta.notificationId}_${meta.leadingBid}_${Date.now()}`
        : `bid_${auctionItem.itemId}_${meta.leadingBid}_${Date.now()}`;

      // Check if this notification already exists in the store
      const currentStore = getStoreData();
      if (currentStore && Array.isArray(currentStore.notifications)) {
        const exists = currentStore.notifications.some(
          (n) => n.id === notificationId
        );
        if (exists) {
          return;
        }

        // Count existing notifications for this item
        const existingForItem = currentStore.notifications.filter(
          (n) => n.data.auctionItem.itemId === parseInt(auctionItem.itemId)
        );
      }

      // Convert WebSocketAuctionItem to AuctionItem format for the store
      const convertedAuctionItem: Partial<AuctionItem> = {
        itemId: parseInt(auctionItem.itemId),
        itemTitle: auctionItem.itemTitle,
        itemPartId: parseInt(auctionItem.itemPartId),
        itemEndTime: auctionItem.itemEndTime,
        images: auctionItem.images
          ? Object.fromEntries(
              auctionItem.images.map((img, idx) => [idx.toString(), img])
            )
          : {},
      };

      // Create notification matching backend structure
      const notification: Notification = {
        id: notificationId,
        type: "bid",
        action: meta.bidAction || "normal",
        meta: meta,
        theme: "white",
        read: false,
        created: new Date().toISOString(),
        isDismissable: true,
        alertAsNotification: true,
        sticky: true,
        data: {
          auctionItem: convertedAuctionItem,
        },
      };

      // Add notification to the store
      addNotification(notification);

      // Log the store state after adding
      logStoreState();

      const toastId = `notification-${notificationId}`;
      const url = getAuctionItemLink(
        notification.data.auctionItem as AuctionItem
      );

      // Set a timeout to automatically dismiss the toast only
      const duration = 10000; // 10 seconds

      const timeoutId = setTimeout(() => {
        toasterQue.dismissToast(toastId);
      }, duration);

      toasterQue.addToastToQueue(
        (t: any) => (
          <div
            className={`${
              t.visible ? "animate-enter" : "animate-leave"
            } max-w-md w-full bg-white border-2 border-solid border-blue text-toast-blue shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5 md:mb-2`}
          >
            <div className="flex-1 w-0 p-3 md:p-4">
              <Link
                href={url}
                className="!no-underline"
                onClick={() => {
                  console.log("Notification clicked");
                  logStoreState();
                }}
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0 pt-0.5">
                    <div className="w-[30px] h-full">
                      {notification.data.auctionItem.images?.[0] ? (
                        <Image
                          src={notification.data.auctionItem.images[0]}
                          alt={notification.data.auctionItem.itemTitle || ""}
                          width={50}
                          height={50}
                          className="object-cover h-[40px]"
                        />
                      ) : (
                        <NoImageFound
                          text=""
                          containerClass="!min-h-full"
                          useIcon
                        />
                      )}
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-xs md:text-sm font-medium text-gray-900">
                      Du blev överbuden på{" "}
                      <strong>{notification.data.auctionItem.itemTitle}</strong>{" "}
                      {meta.leadingBid && (
                        <>nytt ledande bud är {formatPrice(meta.leadingBid)}</>
                      )}
                    </p>
                  </div>
                </div>
              </Link>
            </div>
            <div className="flex border-l border-neutral-200">
              <button
                onClick={() => {
                  console.log("Close button clicked, dismissing toast");
                  toasterQue.dismissToast(toastId);
                  clearTimeout(timeoutId);
                  logStoreState();
                }}
                className="w-10 border border-transparent cursor-pointer rounded-none rounded-r-lg flex items-center justify-center text-lg font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none"
              >
                ×
              </button>
            </div>
          </div>
        ),
        {
          duration: duration,
          id: toastId,
        }
      );
    });

    return () => {
      socket.off(`bid_notification_user_${userId}`);
      socket.off("connect");
      socket.off("disconnect");
      socket.off("connect_error");
    };
  }, [userId, toasterQue]);

  return {
    logStoreState,
    initializeFromPageData,
  };
};
